import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { cache, CacheKeys, CacheTTL } from '@/lib/cache';
import type { Stock, ZoneStockage, CreateStockInput, UpdateStockInput } from '@/types/inventory.types';

const log = createLogger('StockService');

interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service de gestion des stocks
 * Version simplifiée sans mode offline pour éviter les problèmes de synchronisation
 */
class StockService {
  /**
   * Récupérer tous les stocks d'un restaurant (avec cache)
   */
  async getStocks(restaurantId: string, forceRefresh = false): Promise<ServiceResponse<Stock[]>> {
    const cacheKey = CacheKeys.stocks(restaurantId);

    // Vérifier le cache sauf si refresh forcé
    if (!forceRefresh) {
      const cached = cache.get<Stock[]>(cacheKey);
      if (cached) {
        return { success: true, data: cached };
      }
    }

    try {
      const { data, error } = await supabase
        .from('stocks')
        .select('*, produits(*)')
        .eq('restaurant_id', restaurantId)
        .order('derniere_mise_a_jour', { ascending: false });

      if (error) throw error;

      const stocks = data as Stock[];

      // Mettre en cache (TTL court car données dynamiques)
      cache.set(cacheKey, stocks, { ttl: CacheTTL.DYNAMIC });

      return {
        success: true,
        data: stocks,
      };
    } catch (error) {
      log.error('Error fetching stocks:', { error });

      // En cas d'erreur, retourner les données stale si disponibles
      const stale = cache.getStale<Stock[]>(cacheKey);
      if (stale) {
        return { success: true, data: stale };
      }

      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les stocks d'une zone spécifique
   */
  async getStocksByZone(restaurantId: string, zone: ZoneStockage): Promise<ServiceResponse<Stock[]>> {
    try {
      const { data, error } = await supabase
        .from('stocks')
        .select('*, produits(*)')
        .eq('restaurant_id', restaurantId)
        .eq('zone', zone)
        .order('derniere_mise_a_jour', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: data as Stock[],
      };
    } catch (error) {
      log.error('Error fetching stocks by zone:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer le stock d'un produit dans une zone spécifique
   */
  async getStockByProductAndZone(
    produitId: string,
    zone: ZoneStockage,
    _restaurantId?: string
  ): Promise<ServiceResponse<Stock | null>> {
    try {
      const { data, error } = await supabase
        .from('stocks')
        .select('*, produits(*)')
        .eq('produit_id', produitId)
        .eq('zone', zone)
        .single();

      if (error) {
        // Si le stock n'existe pas, ce n'est pas une erreur
        if (error.code === 'PGRST116') {
          return { success: true, data: null };
        }
        throw error;
      }

      return {
        success: true,
        data: data as Stock,
      };
    } catch (error) {
      log.error('Error fetching stock:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Créer un nouveau stock
   */
  async createStock(input: CreateStockInput): Promise<ServiceResponse<Stock>> {
    try {
      const insertData = {
        restaurant_id: input.restaurant_id,
        produit_id: input.produit_id,
        zone: input.zone,
        quantite: input.quantite,
        updated_by: input.updated_by || null,
      };

      const { data, error } = await supabase
        .from('stocks')
        .insert(insertData)
        .select('*, produits(*)')
        .single();

      if (error) throw error;

      // Invalider le cache des stocks
      cache.delete(CacheKeys.stocks(input.restaurant_id));

      return {
        success: true,
        data: data as Stock,
      };
    } catch (error) {
      log.error('Error creating stock:', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Mettre à jour un stock existant
   */
  async updateStock(stockId: string, input: UpdateStockInput, _restaurantId?: string): Promise<ServiceResponse<Stock>> {
    try {
      const { data, error } = await supabase
        .from('stocks')
        .update({
          ...input,
          derniere_mise_a_jour: new Date().toISOString(),
        })
        .eq('id', stockId)
        .select('*, produits(*)')
        .single();

      if (error) throw error;

      // Invalider tous les caches de stocks
      cache.invalidatePrefix('stocks:');

      return {
        success: true,
        data: data as Stock,
      };
    } catch (error) {
      log.error('Error updating stock:', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Ajuster la quantité d'un stock (ajouter ou retirer)
   */
  async adjustQuantity(
    stockId: string,
    quantityChange: number
  ): Promise<ServiceResponse<Stock>> {
    try {
      // Récupérer le stock actuel
      const { data: currentStock, error: fetchError } = await supabase
        .from('stocks')
        .select('quantite')
        .eq('id', stockId)
        .single();

      if (fetchError) throw fetchError;

      const newQuantity = (currentStock.quantite || 0) + quantityChange;

      if (newQuantity < 0) {
        return {
          success: false,
          error: 'La quantité ne peut pas être négative',
        };
      }

      return this.updateStock(stockId, { quantite: newQuantity });
    } catch (error) {
      log.error('Error adjusting stock quantity:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Supprimer un stock
   */
  async deleteStock(stockId: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await supabase.from('stocks').delete().eq('id', stockId);

      if (error) throw error;

      // Invalider tous les caches de stocks
      cache.invalidatePrefix('stocks:');

      return { success: true };
    } catch (error) {
      log.error('Error deleting stock:', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les stocks critiques (stock TOTAL du produit <= seuil_critique)
   */
  async getCriticalStocks(restaurantId: string): Promise<ServiceResponse<Stock[]>> {
    try {
      const stocksResult = await this.getStocks(restaurantId);

      if (!stocksResult.success || !stocksResult.data) {
        return stocksResult as ServiceResponse<Stock[]>;
      }

      const stocks = stocksResult.data;

      // Calculer le stock total par produit
      const stockTotalByProduct: Record<string, number> = {};
      stocks.forEach((stock) => {
        stockTotalByProduct[stock.produit_id] = (stockTotalByProduct[stock.produit_id] || 0) + stock.quantite;
      });

      // Filtrer les produits critiques - un seul par produit
      const seenProducts = new Set<string>();
      const criticalStocks = stocks.filter((stock) => {
        if (!stock.produits || seenProducts.has(stock.produit_id)) return false;
        const stockTotal = stockTotalByProduct[stock.produit_id] || 0;
        const isCritical = stockTotal <= stock.produits.seuil_critique;
        if (isCritical) seenProducts.add(stock.produit_id);
        return isCritical;
      });

      return {
        success: true,
        data: criticalStocks,
      };
    } catch (error) {
      log.error('Error fetching critical stocks:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les stocks en alerte (stock TOTAL > seuil_critique mais <= seuil_alerte)
   */
  async getWarningStocks(restaurantId: string): Promise<ServiceResponse<Stock[]>> {
    try {
      const stocksResult = await this.getStocks(restaurantId);

      if (!stocksResult.success || !stocksResult.data) {
        return stocksResult as ServiceResponse<Stock[]>;
      }

      const stocks = stocksResult.data;

      // Calculer le stock total par produit
      const stockTotalByProduct: Record<string, number> = {};
      stocks.forEach((stock) => {
        stockTotalByProduct[stock.produit_id] = (stockTotalByProduct[stock.produit_id] || 0) + stock.quantite;
      });

      // Filtrer les produits en alerte - un seul par produit
      const seenProducts = new Set<string>();
      const warningStocks = stocks.filter((stock) => {
        if (!stock.produits || seenProducts.has(stock.produit_id)) return false;
        const stockTotal = stockTotalByProduct[stock.produit_id] || 0;
        const isWarning = stockTotal > stock.produits.seuil_critique && stockTotal <= stock.produits.seuil_alerte;
        if (isWarning) seenProducts.add(stock.produit_id);
        return isWarning;
      });

      return {
        success: true,
        data: warningStocks,
      };
    } catch (error) {
      log.error('Error fetching warning stocks:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Créer ou mettre à jour un stock (upsert) - remplace la quantité
   */
  async upsertStock(
    restaurantId: string,
    produitId: string,
    zone: ZoneStockage,
    quantite: number
  ): Promise<ServiceResponse<Stock>> {
    try {
      // Vérifier si le stock existe déjà
      const existingStock = await this.getStockByProductAndZone(produitId, zone, restaurantId);

      if (existingStock.data) {
        return await this.updateStock(existingStock.data.id, { quantite }, restaurantId);
      }

      // Créer
      return await this.createStock({
        restaurant_id: restaurantId,
        produit_id: produitId,
        zone,
        quantite,
      });
    } catch (error) {
      log.error('Error upserting stock:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Ajouter une quantité au stock existant ou créer si n'existe pas
   */
  async addToStock(
    restaurantId: string,
    produitId: string,
    zone: ZoneStockage,
    quantiteToAdd: number
  ): Promise<ServiceResponse<Stock>> {
    try {
      // Vérifier si le stock existe déjà
      const existingStock = await this.getStockByProductAndZone(produitId, zone, restaurantId);

      if (existingStock.data) {
        // Ajouter à la quantité existante
        const newQuantite = existingStock.data.quantite + quantiteToAdd;
        return await this.updateStock(existingStock.data.id, { quantite: newQuantite }, restaurantId);
      }

      // Créer avec la quantité initiale
      return await this.createStock({
        restaurant_id: restaurantId,
        produit_id: produitId,
        zone,
        quantite: quantiteToAdd,
      });
    } catch (error) {
      log.error('Error adding to stock:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer le total des stocks par produit (toutes zones confondues)
   */
  async getTotalStockByProduct(produitId: string): Promise<ServiceResponse<number>> {
    try {
      const { data, error } = await supabase
        .from('stocks')
        .select('quantite')
        .eq('produit_id', produitId);

      if (error) throw error;

      const total = (data || []).reduce((sum, stock) => sum + (stock.quantite || 0), 0);
      return {
        success: true,
        data: total,
      };
    } catch (error) {
      log.error('Error fetching total stock:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les stocks de plusieurs restaurants
   */
  async getStocksForRestaurants(restaurantIds: string[]): Promise<ServiceResponse<Stock[]>> {
    try {
      if (restaurantIds.length === 0) {
        return { success: true, data: [] };
      }

      const { data, error } = await supabase
        .from('stocks')
        .select('*, produits(*)')
        .in('restaurant_id', restaurantIds)
        .order('derniere_mise_a_jour', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: data as Stock[],
      };
    } catch (error) {
      log.error('Error fetching stocks for restaurants:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les statistiques de stocks par restaurant
   */
  async getStockStatsByRestaurant(restaurantIds: string[]): Promise<ServiceResponse<{
    restaurantId: string;
    totalProducts: number;
    totalQuantity: number;
    criticalCount: number;
    warningCount: number;
  }[]>> {
    try {
      const stocksResult = await this.getStocksForRestaurants(restaurantIds);

      if (!stocksResult.success || !stocksResult.data) {
        return { success: false, error: stocksResult.error };
      }

      const stocks = stocksResult.data;

      // Grouper par restaurant
      const statsByRestaurant = new Map<string, {
        restaurantId: string;
        products: Set<string>;
        totalQuantity: number;
        stocksByProduct: Map<string, { total: number; seuil_critique: number; seuil_alerte: number }>;
      }>();

      stocks.forEach((stock) => {
        if (!statsByRestaurant.has(stock.restaurant_id)) {
          statsByRestaurant.set(stock.restaurant_id, {
            restaurantId: stock.restaurant_id,
            products: new Set(),
            totalQuantity: 0,
            stocksByProduct: new Map(),
          });
        }

        const stats = statsByRestaurant.get(stock.restaurant_id)!;
        stats.products.add(stock.produit_id);
        stats.totalQuantity += stock.quantite;

        // Accumuler le stock par produit
        const existing = stats.stocksByProduct.get(stock.produit_id);
        if (existing) {
          existing.total += stock.quantite;
        } else if (stock.produits) {
          stats.stocksByProduct.set(stock.produit_id, {
            total: stock.quantite,
            seuil_critique: stock.produits.seuil_critique,
            seuil_alerte: stock.produits.seuil_alerte,
          });
        }
      });

      // Calculer les alertes
      const result = Array.from(statsByRestaurant.values()).map((stats) => {
        let criticalCount = 0;
        let warningCount = 0;

        stats.stocksByProduct.forEach((productStock) => {
          if (productStock.total <= productStock.seuil_critique) {
            criticalCount++;
          } else if (productStock.total <= productStock.seuil_alerte) {
            warningCount++;
          }
        });

        return {
          restaurantId: stats.restaurantId,
          totalProducts: stats.products.size,
          totalQuantity: stats.totalQuantity,
          criticalCount,
          warningCount,
        };
      });

      return { success: true, data: result };
    } catch (error) {
      log.error('Error fetching stock stats by restaurant:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les statistiques globales de tous les restaurants
   */
  async getGlobalStockStats(restaurantIds: string[]): Promise<ServiceResponse<{
    totalProducts: number;
    totalQuantity: number;
    totalCritical: number;
    totalWarning: number;
    restaurantCount: number;
  }>> {
    try {
      const statsResult = await this.getStockStatsByRestaurant(restaurantIds);

      if (!statsResult.success || !statsResult.data) {
        return { success: false, error: statsResult.error };
      }

      const stats = statsResult.data;

      // Agréger toutes les statistiques
      const globalStats = {
        totalProducts: 0,
        totalQuantity: 0,
        totalCritical: 0,
        totalWarning: 0,
        restaurantCount: stats.length,
      };

      // Pour compter les produits uniques globalement, on doit refaire le calcul
      const allProductIds = new Set<string>();
      const stocksResult = await this.getStocksForRestaurants(restaurantIds);

      if (stocksResult.success && stocksResult.data) {
        stocksResult.data.forEach((stock) => {
          allProductIds.add(stock.produit_id);
        });
      }

      globalStats.totalProducts = allProductIds.size;

      stats.forEach((s) => {
        globalStats.totalQuantity += s.totalQuantity;
        globalStats.totalCritical += s.criticalCount;
        globalStats.totalWarning += s.warningCount;
      });

      return { success: true, data: globalStats };
    } catch (error) {
      log.error('Error fetching global stock stats:', error);
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Formater les erreurs
   */
  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return 'Une erreur inattendue est survenue';
  }
}

// Export d'une instance unique
export const stockService = new StockService();
